import React from 'react'
import isEqual from 'lodash.isequal'
import { Checkbox, DetailInfo } from '@softwaredev_cegeka/stix-form'
import { Tabs } from '@softwaredev_cegeka/stix-core'
import BaseSearchableList from 'src/components/SearchableList'
import { AuthApi } from 'src/api/authApi'
import { authenticatedUserStore } from 'src/stores'

const GROUP_TAB_MAPPINGS = {
    USERS_TAB: {
        entityName: 'user',
        dataField: 'users',
        itemId: 'id',
        param: 'email_like',
        itemDisplay: item => `${item.first_name} ${item.last_name}`,
        detailItemDisplay: item => item.email,
        selectDisplay: item => item.email,
    },
    // TODO: change this to match the actual format of the permissions
    PERMISSIONS_TAB: {
        entityName: 'permission',
        dataField: 'permissions',
        itemId: 'id',
        itemDisplay: item => item.name,
        selectDisplay: item => item.name,
    },
    MAPPED_AD_GROUPS_TAB: {
        entityName: 'mapped_ad_groups',
        dataField: 'mapped_ad_groups',
        itemId: 'id',
        itemDisplay: item => item.ad_query,
        selectDisplay: item => item.ad_query,
    },
    APP_PERMISSIONS_TAB: {
        entityName: 'app_permissions',
        displayEntityName: 'app permission',
        dataField: 'app_permissions',
        itemId: 'id',
        itemDisplay: item => item.application.name,
        selectDisplay: item => item.application.key,
        detailItemDisplay: item =>
            getTenants(item.tenants, item.tenant_is_company_owner),
    },
}

function getTenants(tenants, is_company_owner) {
    if (tenants.length > 0) {
        return tenants.map(tenant => tenant.name).join(', ')
    }
    return is_company_owner
        ? 'All tenants'
        : 'All (parent and subsidiaries) tenants'
}

/**
 * Group Drawer content
 * @returns {*[]}
 */
export default class GroupDrawerContent extends React.Component {
    state = {
        loading: false,
        appPermissionsInfo: {},
    }

    authClient = new AuthApi(authenticatedUserStore.authToken)

    getSwitchProps = column => ({
        srOnly: true,
        type: 'switch',
        disabled: true,
        checked: this.props.selectedGroup[column] ? true : null,
    })

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (!isEqual(prevProps.selectedGroup, this.props.selectedGroup)) {
            this.getAppPermissionData()
        }
    }

    componentWillUnmount() {
        this.authClient.cancelRequest()
    }

    getAppPermissionData = () => {
        this.setState(
            {
                loading: true,
            },
            () => {
                this.authClient
                    .fetchGroupAppPermissions(
                        1,
                        this.props.history,
                        {
                            user_is_admin: true,
                        },
                        this.props.selectedGroup.id
                    )
                    .then(response => {
                        this.setState((_, props) => ({
                            appPermissionsInfo: {
                                ...props.selectedGroup,
                                app_permissions: response?.data?.results,
                            },
                        }))

                        return this.authClient.fetchTenant(
                            this.props.selectedGroup.tenant_id
                        )
                    })
                    .then(response => {
                        this.setState((state, _) => ({
                            appPermissionsInfo: {
                                ...state.appPermissionsInfo,
                                app_permissions:
                                    state.appPermissionsInfo.app_permissions.map(
                                        el => {
                                            return {
                                                ...el,
                                                tenant_is_company_owner:
                                                    response.data
                                                        .is_company_owner,
                                            }
                                        }
                                    ),
                            },
                            loading: false,
                        }))
                    })
                    .catch(err => {
                        this.setState({
                            loading: false,
                        })
                    })
            }
        )
    }

    render() {
        const { selectedGroup, addUser, removeUser } = this.props
        const content = [
            {
                label: 'Tenant',
                value: selectedGroup.tenant?.name,
            },
            {
                label: 'Default',
                value: (
                    <Checkbox
                        {...this.getSwitchProps('isDefault')}
                        label="Default"
                    />
                ),
            },
        ]

        return (
            <>
                <DetailInfo data={content} />
                <Tabs
                    variant="material"
                    defaultActiveKey="users"
                    id="group-drawer-content-tabs"
                >
                    <Tabs.Pane eventKey="users" title="Users">
                        <BaseSearchableList
                            data={selectedGroup}
                            type={GROUP_TAB_MAPPINGS.USERS_TAB}
                            addEntityMethod={addUser}
                            removeEntityMethod={removeUser}
                            confirmationMessage={`Are you sure you want to delete from ${selectedGroup.name} the user`}
                            filteredEntitiesUrl={`/api/auth/tenants/${selectedGroup.tenant_id}/users`}
                            emptyListMessage={`No ${GROUP_TAB_MAPPINGS.USERS_TAB.entityName} linked to this group.`}
                            sortParam={{ sort: '+first_name' }}
                        />
                    </Tabs.Pane>
                    {process.env.REACT_APP_ENABLE_PERMISSIONS_TABS ===
                    'true' ? (
                        <Tabs.Pane eventKey="permissions" title="Permissions">
                            <BaseSearchableList
                                data={selectedGroup}
                                type={GROUP_TAB_MAPPINGS.PERMISSIONS_TAB}
                                emptyListMessage={`No ${GROUP_TAB_MAPPINGS.PERMISSIONS_TAB.entityName} linked to this group.`}
                                sortParam={{ sort: '+name' }}
                            />
                        </Tabs.Pane>
                    ) : (
                        <Tabs.Pane></Tabs.Pane>
                    )}
                    <Tabs.Pane
                        eventKey="mappedAdGroups"
                        title="Mapped AD Groups"
                    >
                        <BaseSearchableList
                            data={selectedGroup}
                            type={GROUP_TAB_MAPPINGS.MAPPED_AD_GROUPS_TAB}
                            emptyListMessage={`No ${GROUP_TAB_MAPPINGS.MAPPED_AD_GROUPS_TAB.entityName} linked to this group.`}
                        />
                    </Tabs.Pane>

                    {/* Invert the outcome of the disabled check, because the string comparison results
                in true if the functionality is available and as an effect of that disables the tab, which we don't want
                in this case */}
                    <Tabs.Pane
                        eventKey="appPermissions"
                        title="App Permissions"
                    >
                        <BaseSearchableList
                            data={this.state.appPermissionsInfo}
                            loading={this.state.loading}
                            optionButton={true}
                            type={GROUP_TAB_MAPPINGS.APP_PERMISSIONS_TAB}
                            emptyListMessage={`No ${GROUP_TAB_MAPPINGS.APP_PERMISSIONS_TAB.entityName} linked to this group.`}
                            addEntityUrl={`/group-form/${selectedGroup.id}/app-permissions-form`}
                            customProp={selectedGroup}
                        />
                    </Tabs.Pane>
                </Tabs>
            </>
        )
    }
}
